import { MessageType } from './types';
import type { ChatbotMessage, StructuredContent } from './types';
import { parseExecutiveCard, type ExecutiveCard, type ExecutiveResultArtifact } from '../../apis/executiveContract';

const DEFAULT_TITLE = 'New Conversation';

const contentText = (content: string | StructuredContent): string => {
  if (typeof content === 'string') return content;
  return (
    content.response || content.message || content.text || content.answer || ''
  );
};

export interface MonitorAlertPresentation { text: string; artifact: ExecutiveResultArtifact }

/** Read only the allow-listed monitor evidence block, never display its hidden instructions. */
export function getMonitorAlerts(message: ChatbotMessage): MonitorAlertPresentation[] {
  if (!message.harness) return [];
  const text = contentText(message.content).slice(0, 128_000);
  const alerts: MonitorAlertPresentation[] = [];
  const blocks = /<MONITOR_CROSSINGS?\b[^>]*>([\s\S]*?)<\/MONITOR_CROSSINGS?>/g;
  for (const match of text.matchAll(blocks)) {
    try {
      const data = JSON.parse(match[1]);
      const rows = Array.isArray(data?.crossings) ? data.crossings : [data];
      for (const row of rows) {
        if (!row || typeof row.monitor_id !== 'string' || !/^[A-Za-z0-9_.:-]{1,128}$/.test(row.monitor_id) ||
            typeof row.monitor_name !== 'string' || typeof row.current_value !== 'string' ||
            typeof row.threshold !== 'string' || !['rose above', 'fell below'].includes(row.crossing)) continue;
        const name = row.monitor_name.slice(0, 256) || 'Monitor';
        alerts.push({ text: `${name} ${row.crossing} ${row.threshold.slice(0, 128)}; observed ${row.current_value.slice(0, 128)}`,
          artifact: { type: 'monitor', id: row.monitor_id, name } });
        if (alerts.length === 12) return alerts;
      }
    } catch { /* A malformed evidence block is not permission to reveal the hidden message. */ }
  }
  return alerts;
}

export const getChatMessageDisplayText = (message: ChatbotMessage): string =>
  typeof message.display === 'string' ? message.display.trim() : '';

/**
 * The envelope a returned scheduled run is saved in
 * (`scheduled_run_return.RETURN_MARKER`, pinned by
 * tests/test_scheduled_run_return.py). It recognises
 * returns saved before the server stamped `origin: "harness"`.
 */
export const SCHEDULED_RUN_RETURN_OPEN_TAG = '<SCHEDULED_RUN_RESULT ';

/**
 * Whether a saved user-role message was written by the harness rather than the
 * person: a woken turn's prompt, a returned run, a failed-wake notice. The
 * server says so with `origin: "harness"`; older saves are recognised by the
 * returned run's envelope.
 */
export const isHarnessAuthored = (saved: {
  role?: unknown;
  origin?: unknown;
  is_hidden?: unknown;
  message?: unknown;
}): boolean =>
  saved.role === 'user' &&
  (saved.origin === 'harness' ||
    (Boolean(saved.is_hidden) &&
      typeof saved.message === 'string' &&
      saved.message.startsWith(SCHEDULED_RUN_RETURN_OPEN_TAG)));

type SavedStepMarker = { tool?: unknown; message?: unknown; success?: unknown; run_id?: unknown };

/**
 * A reply's steps as its Working pill showed them live, from the `step_markers`
 * the server saves beside it (Code Mode keeps no tool messages to rebuild them
 * from). A step the turn ended on is done, as the live stream's close marks it.
 */
export const savedStepMarkers = (stepMarkers: unknown): ChatbotMessage['toolMarkers'] => {
  if (!Array.isArray(stepMarkers)) return undefined;
  const markers = stepMarkers
    .filter((m): m is SavedStepMarker => Boolean(m) && typeof m === 'object')
    .map(m => {
      const tool = typeof m.tool === 'string' && m.tool ? m.tool : 'System Tool';
      return {
        position: 0,
        tool,
        runId: typeof m.run_id === 'string' ? m.run_id : undefined,
        message: typeof m.message === 'string' && m.message ? m.message : `using ${tool}`,
        success: m.success !== false,
      };
    });
  return markers.length > 0 ? markers : undefined;
};

/** How a saved message presents: what the history loader copies onto the chat message. */
export const savedPresentation = (saved: {
  role?: unknown;
  origin?: unknown;
  is_hidden?: unknown;
  message?: unknown;
  display_text?: unknown;
  turn_id?: unknown;
  msg_ids?: unknown;
  send_ids?: unknown;
  delivered_mid_turn?: unknown;
  step_markers?: unknown;
  executive_card?: unknown;
}): Pick<ChatbotMessage, 'hidden' | 'display' | 'harness' | 'turnId' | 'msgIds' | 'sendIds' | 'inboxStatus' | 'toolMarkers' | 'executiveCard'> => ({
  turnId: typeof saved.turn_id === 'string' ? saved.turn_id : undefined,
  msgIds: Array.isArray(saved.msg_ids) ? saved.msg_ids.filter((id): id is string => typeof id === 'string' && Boolean(id)) : undefined,
  sendIds: Array.isArray(saved.send_ids) ? saved.send_ids.filter((id): id is string => typeof id === 'string' && Boolean(id)) : undefined,
  // Sent while a turn ran and read by it: labelled on reload as it was live.
  inboxStatus: saved.delivered_mid_turn === true ? 'read' : undefined,
  hidden: Boolean(saved.is_hidden),
  display: typeof saved.display_text === 'string' && saved.display_text ? saved.display_text : undefined,
  harness: isHarnessAuthored(saved) || undefined,
  // The reply's steps, so its pill says the same "N steps used" it said live.
  toolMarkers: savedStepMarkers(saved.step_markers),
  // The executive's harness card; only a harness message can carry one.
  executiveCard: isHarnessAuthored(saved) ? parseExecutiveCard(saved.executive_card) : undefined,
});

/**
 * Hidden messages keep their full payload available to Leo. A non-empty
 * display override is the safe, user-facing substitute for that payload; a
 * hidden message without one remains completely invisible.
 */
export const shouldRenderChatMessage = (message: ChatbotMessage): boolean =>
  !message.hidden || Boolean(getChatMessageDisplayText(message)) || Boolean(message.harness && message.executiveCard) || getMonitorAlerts(message).length > 0;

/** The line a woken reply's Working pill opens with. */
export const WOKEN_REPLY_NOTE = 'Resumed after the background read';
// One array, so a memoized reply does not re-render on every streamed chunk.
const WOKEN_REPLY_NOTES: readonly string[] = Object.freeze([WOKEN_REPLY_NOTE]);

/** A reply the harness started after a background read finished (no new message from the person). */
export const isWokenReply = (message: ChatbotMessage): boolean =>
  message.type === MessageType.ASSISTANT && Boolean(message.turnId?.startsWith('continuation_'));

export type ChatTimelineItem =
  | { kind: 'message'; message: ChatbotMessage; pillNotes?: readonly string[] }
  | { kind: 'notice'; message: ChatbotMessage; text: string }
  | { kind: 'executive_card'; message: ChatbotMessage; card: ExecutiveCard };

/**
 * What the chat shows, in order — one derivation for a live chat and a
 * reloaded one, so they cannot drift apart.
 *
 * - The person's messages (and hidden sends they chose, via their display
 *   text) are bubbles.
 * - What the harness wrote is never a bubble: an executive card renders as a
 *   card; otherwise without a display line it is not shown at all, and with
 *   one it is a quiet notice on Leo's side.
 * - A reply the harness woke carries that fact inside its collapsed pill.
 */
export function buildChatTimeline(messages: readonly ChatbotMessage[]): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = [];
  for (const message of messages) {
    if (!shouldRenderChatMessage(message)) continue;
    if (message.type === MessageType.USER && message.harness) {
      if (message.executiveCard) {
        items.push({ kind: 'executive_card', message, card: message.executiveCard });
        continue;
      }
      const text = getMonitorAlerts(message).map(alert => alert.text).join(' · ') || getChatMessageDisplayText(message);
      if (text) items.push({ kind: 'notice', message, text });
      continue;
    }
    items.push(isWokenReply(message)
      ? { kind: 'message', message, pillNotes: WOKEN_REPLY_NOTES }
      : { kind: 'message', message });
  }
  return items;
}

/**
 * Derive the temporary client title from the same text the user can see.
 * Never expose a hidden model-only payload through the tab/history UI, and
 * never title a chat after something the harness wrote.
 */
export const deriveConversationTitle = (
  messages: ChatbotMessage[],
  maxCharacters = 50,
  ellipsis = '...',
): string => {
  for (const message of messages) {
    if (message.type !== MessageType.USER || message.harness) continue;

    const displayText = getChatMessageDisplayText(message);
    if (displayText) {
      return displayText.length > maxCharacters
        ? `${displayText.slice(0, maxCharacters)}${ellipsis}`
        : displayText;
    }
    if (message.hidden) continue;

    const visibleContent = contentText(message.content)
      .replace(/<[^>]+>/g, '')
      .trim();
    if (!visibleContent) continue;
    return visibleContent.length > maxCharacters
      ? `${visibleContent.slice(0, maxCharacters)}${ellipsis}`
      : visibleContent;
  }

  return DEFAULT_TITLE;
};
